import AddCircleIcon from '@mui/icons-material/AddCircle';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import { Box, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { addAlphaToColor } from 'shared/utils/colors';

import { TeamIcon } from '@/assets/svg';
import { MembersSection } from '@/pages/TeamsPage/components';
import { ROUTE } from '@/router/routes';
import { t } from '@/services/translations/config';
import { COLOR } from '@/theme/colors';

import { useSubscriptionTeamSeats } from './useSubscriptionTeamSeats';

const SubscriptionTeamSeats: React.FC<{
    isTeamCreated: boolean;
}> = ({ isTeamCreated }) => {
    const navigate = useNavigate();
    const {
        currentPlanSeats,
        pendingInvitationsCount,
        unusedSeats,
        canInviteMembers,
        members,
    } = useSubscriptionTeamSeats({ isTeamCreated });

    const isTeamsSubscription = currentPlanSeats > 1;

    if (!isTeamsSubscription || !isTeamCreated) {
        return null;
    }

    const handleInviteTeamMembersPress = () => {
        if (!canInviteMembers) return;

        if (!isTeamCreated) {
            navigate(ROUTE.teams);
            return;
        }

        navigate(ROUTE.teams, {
            state: {
                openInvitationModal: true,
                redirectToOnCloseInvitationModal: ROUTE.premium,
            },
        });
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                mx: 'auto',
                maxWidth: '1300px',
                width: '100%',
                px: '24px',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    maxWidth: '600px',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <TeamIcon color={COLOR.black} height={24} width={24} />
                    <Typography
                        fontSize={16}
                        fontWeight={700}
                        color={COLOR.ebonyClay}
                    >
                        {t('SubscriptionDetails:team_seats:title')}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '18px',
                        border: `1px solid ${COLOR.lightGray}`,
                        padding: '18px',
                        borderRadius: '8px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography
                            fontSize={14}
                            fontWeight={700}
                            color={COLOR.ebonyClay}
                        >
                            {t('SubscriptionDetails:team_seats:total_seats', {
                                currentPlanSeats,
                            })}
                        </Typography>

                        <Typography
                            color={COLOR.deepCerulean}
                            fontSize={12}
                            component="button"
                            onClick={() => navigate(ROUTE.teams)}
                        >
                            {t('SubscriptionDetails:team_seats:manage_team')}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            backgroundColor: COLOR.foam,
                            borderRadius: '8px',
                            padding: '16px',
                        }}
                    >
                        <InfoOutlineIcon
                            style={{
                                width: 20,
                                height: 20,
                                color: COLOR.deepCerulean,
                            }}
                        />
                        <Typography fontSize={14} color={COLOR.astronautBlue}>
                            {t(
                                'SubscriptionDetails:team_seats:manage_subscription',
                            )}
                        </Typography>
                    </Box>

                    <Box sx={{ mx: '-16px', mt: '-20px' }}>
                        <MembersSection members={members} hideTitle />
                    </Box>

                    {Array.from({ length: pendingInvitationsCount }).map(
                        (_, index) => (
                            <Box
                                key={`pending-invitation-${index}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderRadius: '8px',
                                    borderTopLeftRadius: 30,
                                    borderBottomLeftRadius: 30,
                                    border: `1px solid ${COLOR.desktopGradientGrey}`,
                                    boxSizing: 'border-box',
                                    paddingLeft: '76px',
                                    paddingRight: '15px',
                                    position: 'relative',
                                    minHeight: '64px',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        backgroundColor:
                                            COLOR.desktopGradientGrey,
                                        borderRadius: '50px',
                                        width: '64px',
                                        height: '64px',
                                        position: 'absolute',
                                        left: '-1px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                    }}
                                >
                                    <AddCircleIcon
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            color: COLOR.mediumGray,
                                        }}
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '100%',
                                    }}
                                >
                                    <Typography
                                        fontWeight={700}
                                        fontSize={12}
                                        color={addAlphaToColor(
                                            COLOR.black,
                                            0.5,
                                        )}
                                    >
                                        {t(
                                            'SubscriptionDetails:team_seats:pending_invitation',
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        ),
                    )}

                    {Array.from({
                        length: unusedSeats,
                    }).map((_, index) => (
                        <Box
                            key={`unused-seat-${index}`}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                borderRadius: '8px',
                                borderTopLeftRadius: 30,
                                borderBottomLeftRadius: 30,
                                border: `1px solid ${COLOR.desktopGradientGrey}`,
                                boxSizing: 'border-box',
                                paddingLeft: '76px',
                                paddingRight: '15px',
                                position: 'relative',
                                minHeight: '64px',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    backgroundColor: COLOR.desktopGradientGrey,
                                    borderRadius: '50px',
                                    width: '64px',
                                    height: '64px',
                                    position: 'absolute',
                                    left: '-1px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                }}
                            >
                                <AddCircleIcon
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        color: COLOR.mediumGray,
                                    }}
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%',
                                }}
                            >
                                <Typography
                                    fontWeight={700}
                                    fontSize={12}
                                    color={addAlphaToColor(COLOR.black, 0.5)}
                                >
                                    {t(
                                        'SubscriptionDetails:team_seats:unused_seat',
                                    )}
                                </Typography>
                                <Typography
                                    color={
                                        canInviteMembers
                                            ? COLOR.deepCerulean
                                            : COLOR.mediumGray
                                    }
                                    fontSize={12}
                                    component="button"
                                    onClick={handleInviteTeamMembersPress}
                                    sx={{
                                        cursor: canInviteMembers
                                            ? 'pointer'
                                            : 'not-allowed',
                                        opacity: canInviteMembers ? 1 : 0.6,
                                    }}
                                >
                                    {t(
                                        'SubscriptionDetails:team_seats:invite_team_member',
                                    )}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default SubscriptionTeamSeats;
